import Vue from 'vue';
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { IClassContentPreferences } from '../interfaces/class-content-preferences.interface';
import { IContentPreferences } from '../interfaces/content-preferences.interface';
import { IOrganizationPreferences } from '../interfaces/organization-preferences.interface';
import { Preference } from '../models/preference.model';
import { IUserContentPreferencesParams } from '../params/user-content-preferences.params';
import { PreferencesService } from '../services/preferences.service';
import { PREFERENCES_MUTATIONS } from '../values/preferences.values';

@Module({ namespaced: true })
export class PreferencesStore extends VuexModule {

  /* State */
  public organizationPreferences: IOrganizationPreferences[] = [];
  public classContentPreferences: IClassContentPreferences[] = [];
  public userContentPreferences: IClassContentPreferences[] = [];
  public contentPreferences: IContentPreferences[] = [];

  /* Mutations */
  @Mutation
  public setOrganizationPreferences(data: IOrganizationPreferences) {
    let i = this.organizationPreferences
      .findIndex(p => p.organizationId == data.organizationId);
    if(i == -1) {
      this.organizationPreferences.push(data);
    } else {
      Vue.set(this.organizationPreferences, i, data);
    }
  }

  @Mutation
  public updateOrganizationPreferences(data: IOrganizationPreferences) {
    let i = this.organizationPreferences
      .findIndex(p => p.organizationId == data.organizationId);
    if(i !== -1) {
      let preferences: Preference[] = this.organizationPreferences[i].preferences;
      let preferencesUpdated = preferences.map(p => {
        let pref = p;
        let j = data.preferences.findIndex(pu => p.preferenceName == pu.preferenceName);
        if(j !== -1) pref = data.preferences[j];
        return pref
      });
      Vue.set(this.organizationPreferences[i], 'preferences', preferencesUpdated);
    }
  }

  @Mutation
  public setClassContentPreferences(data: IClassContentPreferences) {
    let i = this.classContentPreferences
      .findIndex(p => p.classId == data.classId && p.contentId == data.contentId);
    if(i == -1) {
      this.classContentPreferences.push(data);
    } else {
      Vue.set(this.classContentPreferences, i, data);
    }
  }

  @Mutation
  public setUserContentPreferences(data: IClassContentPreferences) {
    let i = this.userContentPreferences
      .findIndex(p => p.classId == data.classId && p.contentId == data.contentId);
    if(i == -1) {
      this.userContentPreferences.push(data);
    } else {
      Vue.set(this.userContentPreferences, i, data);
    }
  }

  @Mutation
  public updateUserContentPreferences(data: IClassContentPreferences) {
    let i = this.userContentPreferences
      .findIndex(p => p.classId == data.classId && p.contentId == data.contentId);
    let preferences: Preference[] = this.userContentPreferences[i].preferences;
    let preferencesUpdated = preferences.map(p => {
      let pref = p;
      let j = data.preferences.findIndex(pu => p.preferenceName == pu.preferenceName);
      if(j !== -1) pref = data.preferences[j];
      return pref
    });
    Vue.set(this.userContentPreferences[i], 'preferences', preferencesUpdated);
  }

  /* Actions */
  @Action({ rawError: true })
  public async getOrganizationPreferences(organizationId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      PreferencesService.getOrganizationPreferences(organizationId)
        .then(preferences => {
          let data: IOrganizationPreferences = {
            organizationId,
            preferences
          }
          this.context.commit(
            PREFERENCES_MUTATIONS.SET_ORGANIZATION_PREFERENCES,
            data
          );
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public async saveOrganizationPreferences(data: IOrganizationPreferences): Promise<void> {
    return new Promise((resolve, reject) => {
      PreferencesService.setOrganizationPreferences(data.preferences, data.organizationId)
        .then(() => {
          this.context.commit(
            PREFERENCES_MUTATIONS.UPDATE_ORGANIZATION_PREFERENCES,
            data
          );
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public async getUserContentPreferences(params: IUserContentPreferencesParams): Promise<void> {
    return new Promise((resolve, reject) => {
      PreferencesService.getUserContentPreferences(params)
        .then(preferences => {
          this.context.commit(
            PREFERENCES_MUTATIONS.SET_USER_CONTENT_PREFERENCES,
            {
              classId: params.classId,
              contentId: params.contentId,
              preferences
            }
          );
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public async getContentPreferences(contentId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      PreferencesService.getContentPreferences(contentId)
        .then(preferences => {
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public async getClassContentPreferences({classId, contentId}): Promise<void> {
    return new Promise((resolve, reject) => {
      PreferencesService.getClassContentPreferences(classId, contentId)
        .then(preferences => {
          this.context.commit(
            PREFERENCES_MUTATIONS.SET_CLASS_CONTENT_PREFERENCES,
            {
              classId,
              contentId,
              preferences
            }
          );
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  @Action({ rawError: true })
  public async saveUserContentPreferences({
    preferences,
    params
  }): Promise<void> {
    return new Promise((resolve, reject) => {
      PreferencesService.setUserContentPreferences(preferences, params)
        .then(() => {
          this.context.commit(
            PREFERENCES_MUTATIONS.UPDATE_USER_CONTENT_PREFERENCES,
            {
              preferences,
              classId: params.classId, 
              contentId: params.contentId
            }
          );
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /* Initial State */
  @Mutation
  public initState(state) {
    this.organizationPreferences = [];
    this.classContentPreferences = [];
    this.userContentPreferences = [];
    this.contentPreferences = [];
  }

}
